import { OPS_PAYMENT_PROOF_MAX_BYTES } from '@utazzo/shared';
import { cn, UzBadge, UzButton, UzDialog, UzInput, UzSpinner, UzTabs } from '@utazzo/ui';
import { FileImage, HandCoins, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { fileToBase64 } from '../file-base64';
import { itemKindLabel } from '../item-kind';
import { payByTone } from '../pay-by';
import { useFundRequests, useMarkFundPaid, useOpenOpsProof } from '../use-ops-cases';

import type { OpsItemQueueRow, OpsLeadMoney } from '@utazzo/shared';

import { PageShell } from '@/components/page-shell';

/**
 * Accounts' queue: supplier payments operations asked for while issuing a
 * converted lead's bookings - one row per instalment when ops split the
 * payment, each with its own pay-by. Pay the supplier, upload the payment
 * screenshot, mark paid; once every instalment of an item is paid the ops
 * owner is notified to get the confirmation and issue.
 */

type View = 'requested' | 'paid';

const money = (n: number | null, c: string | null): string =>
  n === null ? '-' : `${c ?? 'INR'} ${new Intl.NumberFormat('en-IN').format(n)}`;
const inr = (n: number | null): string =>
  n === null ? '' : `INR ${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(n)}`;
const when = (d: Date | null): string =>
  d
    ? d.toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';
const day = (d: Date | null): string =>
  d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '';

/** The customer's side of a lead next to a supplier payment: paid, owed, and what is in hand. */
const LeadMoneyCell = ({ m }: { m: OpsLeadMoney | null }): React.JSX.Element => {
  if (!m) return <span className="text-xs text-zinc-400">-</span>;
  const short = m.availableInr < 0;
  return (
    <div className="space-y-0.5 text-xs whitespace-nowrap">
      <div className="text-zinc-800">
        Received <span className="font-semibold tabular-nums">{inr(m.receivedInr)}</span>
        {m.saleInr !== null ? (
          <span className="text-zinc-500"> of {inr(m.saleInr)}</span>
        ) : (
          <span className="text-zinc-400"> (no sale price)</span>
        )}
      </div>
      {m.balanceInr !== null ? (
        <div className={cn(m.balanceInr > 0 ? 'text-amber-700' : 'text-emerald-700')}>
          {m.balanceInr > 0 ? `Customer still owes ${inr(m.balanceInr)}` : 'Customer paid in full'}
        </div>
      ) : null}
      <div className="text-zinc-600">
        Paid to suppliers <span className="tabular-nums">{inr(m.utilisedInr)}</span> · asked{' '}
        <span className="tabular-nums">{inr(m.committedInr)}</span>
      </div>
      <div className={cn('font-medium', short ? 'text-red-700' : 'text-emerald-700')}>
        {short ? `Short by ${inr(Math.abs(m.availableInr))}` : `In hand ${inr(m.availableInr)}`}
      </div>
    </div>
  );
};

const ACCEPT = 'image/png,image/jpeg,image/webp,application/pdf,.png,.jpg,.jpeg,.webp,.pdf';

const MarkPaidDialog = ({
  row,
  onClose,
}: {
  row: OpsItemQueueRow | null;
  onClose: () => void;
}): React.JSX.Element => {
  const markPaid = useMarkFundPaid();
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | null>(null);

  const [tried, setTried] = useState(false);
  const reset = (): void => {
    setFile(null);
    setReference('');
    setError(null);
    setTried(false);
  };

  const submit = async (): Promise<void> => {
    if (!row) return;
    setTried(true);
    if (!reference.trim()) {
      setError('Enter the payment reference / UTR.');
      return;
    }
    if (!file) {
      setError('Upload the payment screenshot.');
      return;
    }
    if (file.size > OPS_PAYMENT_PROOF_MAX_BYTES) {
      setError('The file is too large (max 8MB).');
      return;
    }
    const proofBase64 = await fileToBase64(file);
    markPaid.mutate(
      {
        caseId: row.opsCaseId,
        componentId: row.componentId,
        input: {
          ...(row.instalment ? { instalmentId: row.instalment.id } : {}),
          proofFileName: file.name,
          proofBase64,
          paymentReference: reference.trim(),
        },
      },
      {
        onSuccess: () => {
          reset();
          onClose();
        },
      },
    );
  };

  return (
    <UzDialog
      open={row !== null}
      onOpenChange={(o) => {
        if (!o) {
          reset();
          onClose();
        }
      }}
      title="Mark supplier paid"
      description={
        row
          ? `${
              row.instalment && row.instalment.count > 1
                ? `${money(row.instalment.amount, row.instalment.currency)} (instalment ${row.instalment.seq} of ${row.instalment.count}, total ${money(row.fundAmount, row.fundCurrency)})`
                : money(row.fundAmount, row.fundCurrency)
            } to ${row.supplierName ?? 'supplier'} · ${row.itemTitle} · ${row.leadReference}`
          : undefined
      }
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={onClose} disabled={markPaid.isPending}>
            Cancel
          </UzButton>
          <UzButton loading={markPaid.isPending} onClick={() => void submit()}>
            <HandCoins className="mr-1.5 h-4 w-4" /> Mark paid
          </UzButton>
        </div>
      }
    >
      <div className="space-y-3">
        <UzInput
          label="Payment reference / UTR"
          required
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          error={tried && !reference.trim() ? 'Enter the payment reference / UTR' : undefined}
        />
        <div>
          <div className="mb-1 text-sm font-medium text-zinc-700">
            Payment screenshot <span className="text-red-600">*</span>
          </div>
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className={cn(
              'flex w-full items-center gap-3 rounded-lg border-2 border-dashed px-4 py-4 text-left text-sm',
              file ? 'border-emerald-300 bg-emerald-50' : 'border-zinc-300 hover:bg-zinc-50',
            )}
          >
            <Upload className="h-5 w-5 text-zinc-500" />
            <span className="min-w-0 flex-1 truncate">
              {file ? file.name : 'Choose a PNG, JPG, WebP or PDF (max 8MB)'}
            </span>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept={ACCEPT}
            className="sr-only"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setError(null);
              e.target.value = '';
            }}
          />
          {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
        </div>
      </div>
    </UzDialog>
  );
};

const RequestsTable = ({ view }: { view: View }): React.JSX.Element => {
  const query = useFundRequests(view);
  const proof = useOpenOpsProof();
  const [paying, setPaying] = useState<OpsItemQueueRow | null>(null);
  const rows = query.data ?? [];

  if (query.isLoading) {
    return (
      <div className="flex items-center gap-2 py-6 text-sm text-zinc-500">
        <UzSpinner size="sm" /> Loading…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="rounded-xl border border-dashed border-zinc-200 py-10 text-center text-sm text-zinc-500">
        {view === 'requested' ? 'No payments waiting.' : 'Nothing paid yet.'}
      </p>
    );
  }

  return (
    <>
      <div className="overflow-x-auto rounded-xl border border-zinc-200 bg-white">
        <table className="w-full min-w-[1100px] text-sm">
          <thead className="bg-zinc-50 text-left text-xs text-zinc-500">
            <tr>
              <th className="px-3 py-2 font-medium">{view === 'requested' ? 'Asked' : 'Paid'}</th>
              <th className="px-3 py-2 font-medium">Lead</th>
              <th className="px-3 py-2 font-medium">Item</th>
              <th className="px-3 py-2 font-medium">Supplier</th>
              <th className="px-3 py-2 font-medium">Customer</th>
              <th className="px-3 py-2 text-right font-medium">Amount</th>
              <th className="px-3 py-2 font-medium">Pay by</th>
              <th className="px-3 py-2 font-medium">Ops</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const due = r.paymentDueDate ? payByTone(r.paymentDueDate) : null;
              return (
                <tr
                  key={`${r.opsCaseId}:${r.componentId}:${r.instalment?.id ?? 'item'}`}
                  className="border-t border-zinc-100 align-top"
                >
                  <td className="px-3 py-2.5 text-xs whitespace-nowrap text-zinc-600">
                    {when(view === 'requested' ? r.fundRequestedAt : r.paidAt)}
                    {view === 'paid' && r.paidByName ? (
                      <div className="text-zinc-400">by {r.paidByName}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-zinc-900">{r.customerName}</div>
                    <div className="text-xs text-zinc-500">{r.leadReference}</div>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="text-zinc-800">{r.itemTitle}</div>
                    <div className="text-xs text-zinc-500">
                      {itemKindLabel(r.kind, r.vasKind)}
                      {r.holdReference ? ` · hold ${r.holdReference}` : ''}
                    </div>
                    {r.note ? (
                      <div className="mt-0.5 text-xs text-zinc-500 italic">{r.note}</div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2.5 text-zinc-800">{r.supplierName ?? '-'}</td>
                  <td className="px-3 py-2.5">
                    <LeadMoneyCell m={r.leadMoney} />
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {r.instalment && r.instalment.count > 1 ? (
                      <>
                        <div className="font-semibold text-zinc-900">
                          {money(r.instalment.amount, r.instalment.currency)}
                        </div>
                        <div className="text-xs text-zinc-500">
                          Instalment {r.instalment.seq} of {r.instalment.count}
                          {r.instalment.label ? ` · ${r.instalment.label}` : ''} · total{' '}
                          {money(r.fundAmount, r.fundCurrency)}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-semibold text-zinc-900">
                          {money(r.fundAmount, r.fundCurrency)}
                        </div>
                        {r.fundCurrency && r.fundCurrency !== 'INR' ? (
                          <div className="text-xs text-zinc-500">{inr(r.fundInr)}</div>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="px-3 py-2.5 whitespace-nowrap">
                    {r.paymentDueDate && due ? (
                      <span
                        className={cn(
                          'rounded px-1.5 py-0.5 text-xs font-medium ring-1',
                          due.className,
                        )}
                      >
                        {day(r.paymentDueDate)} · {due.label}
                      </span>
                    ) : (
                      <span className="text-xs text-zinc-400">-</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-xs text-zinc-600">
                    {r.opsOwnerName ?? r.requestedByName ?? '-'}
                  </td>
                  <td className="px-3 py-2.5 text-right whitespace-nowrap">
                    {view === 'requested' ? (
                      <UzButton size="sm" onClick={() => setPaying(r)}>
                        <HandCoins className="mr-1.5 h-3.5 w-3.5" /> Mark paid
                      </UzButton>
                    ) : (
                      <div className="flex items-center justify-end gap-2">
                        {r.status === 'ISSUED' ? (
                          <UzBadge tone="success">Issued</UzBadge>
                        ) : r.instalment && r.instalment.paidCount < r.instalment.count ? (
                          <UzBadge tone="warning">
                            {r.instalment.paidCount} of {r.instalment.count} paid
                          </UzBadge>
                        ) : (
                          <UzBadge tone="default">Awaiting issue</UzBadge>
                        )}
                        {r.hasPaymentProof ? (
                          <UzButton
                            size="sm"
                            variant="ghost"
                            title="Payment screenshot"
                            onClick={() =>
                              proof.mutate({
                                caseId: r.opsCaseId,
                                componentId: r.componentId,
                                instalmentId: r.instalment?.id,
                                viaAccounts: true,
                              })
                            }
                          >
                            <FileImage className="h-3.5 w-3.5" />
                          </UzButton>
                        ) : null}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <MarkPaidDialog row={paying} onClose={() => setPaying(null)} />
    </>
  );
};

export const FundRequestsPage = (): React.JSX.Element => {
  const [params, setParams] = useSearchParams();
  const view: View = params.get('view') === 'paid' ? 'paid' : 'requested';
  const open = useFundRequests('requested');

  return (
    <PageShell
      title="Fund requests"
      description="Supplier payments operations asked for while issuing bookings. Pay, upload the screenshot, mark paid."
    >
      <UzTabs
        value={view}
        onValueChange={(v) => setParams(v === 'paid' ? { view: 'paid' } : {}, { replace: true })}
        tabs={[
          {
            value: 'requested',
            label: 'To pay',
            count: open.data?.length,
            content: <RequestsTable view="requested" />,
          },
          { value: 'paid', label: 'Paid', content: <RequestsTable view="paid" /> },
        ]}
      />
    </PageShell>
  );
};
